import * as act from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        caption: "flex justify-center pt-1 relative items-center",
        caption_label: "text-sm font-medium",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex space-x-1",
        head_cell:
          "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
        row: "flex w-full mt-2 space-x-1", // Added space-x-1 here
        cell: cn(
          "h-9 w-9 text-center text-sm p-0 relative",
          "focus-within:relative focus-within:z-20",
          "[&:has([aria-selected])]:bg-transparent" // Remove default background
        ),
        day: cn(
          buttonVariants({ variant: "ghost" }),
          "h-9 w-9 p-0 font-normal aria-selected:opacity-100 rounded-md", // Added rounded-md
          "[&.day-range-start]:bg-indigo-300 [&.day-range-start]:text-white hover:[&.day-range-start]:bg-indigo-300", // Style for start date
          "[&.day-range-end]:bg-indigo-300 [&.day-range-end]:text-white hover:[&.day-range-end]:bg-indigo-300", // Style for end date
          "[&.day-range-middle]:bg-indigo-100 [&.day-range-middle]:text-indigo-300 hover:[&.day-range-middle]:bg-indigo-200" // Style for middle dates
        ),
        day_range_start: "day-range-start",
        day_range_end: "day-range-end",
        day_range_middle: "day-range-middle",
        day_selected:
          "bg-indigo-300 text-white hover:bg-indigo-300 hover:text-white focus:bg-indigo-300 focus:text-white",
        day_today: "bg-accent text-accent-foreground",
        day_outside:
          "text-muted-foreground opacity-50 aria-selected:opacity-30",
        day_disabled: "",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: ({ ...props }) => <ChevronLeft className="h-4 w-4" />,
        IconRight: ({ ...props }) => <ChevronRight className="h-4 w-4" />,
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
